import React from "react";
import { formatServiceCost, serviceCostSummary } from "../../lib/serviceCost";

export default function ServiceCostReview({ mutation, preview, original, draft }) {
  const customCostCommands = mutation?.custom_cost_commands || [];
  const hasChanges = customCostCommands.length > 0 || Boolean(mutation?.discount);
  if (!hasChanges) return null;

  const summary = serviceCostSummary(preview);
  const originalCustomCosts = new Map(
    (original?.custom_costs || []).map((row) => [row.id, row])
  );
  const originalDiscount = original?.discount || {};
  const discountState = (discount) => {
    const status = discount?.enabled === true ? "ON" : "OFF";
    const percentage = String(discount?.percentage ?? "0").trim() || "0";
    const reason = String(discount?.reason ?? "").trim();
    return `${status} · ${percentage}%${reason ? ` · ${reason}` : ""}`;
  };
  const draftRows = draft?.custom_costs || [];
  const finalRowFor = (command) => {
    if (command.id) return draftRows.find((row) => row.id === command.id) || command;
    return draftRows.find((row) =>
      !row.id && row.description === command.description &&
      String(row.quantity) === String(command.quantity) &&
      String(row.unit_price) === String(command.unit_price)
    ) || command;
  };

  return (
    <section data-testid="review-service-cost">
      <h3 className="text-sm font-semibold text-zinc-950 dark:text-zinc-100">Service Cost Changes</h3>
      <ul className="mt-3 space-y-2 rounded-sm border border-zinc-200 p-3 text-sm dark:border-zinc-800">
        {customCostCommands.map((command, index) => {
          const originalRow = originalCustomCosts.get(command.id);
          const finalRow = command.action === "remove" ? null : finalRowFor(command);
          const description = finalRow?.description || command.description || originalRow?.description || command.id;
          const originalAmount = originalRow?.line_total;
          const nextAmount = finalRow ? preview?.customLineTotals?.[finalRow._key || finalRow.id] : undefined;
          if (command.action === "edit") {
            return (
              <li key={`${command.action}:${command.id}`} data-testid={`review-custom-cost-${command.action}-${command.id}`}>
                <div className="font-medium">Edit Custom Cost</div>
                <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                  <dt className="text-zinc-500">Description</dt><dd>{originalRow?.description || "—"} → {command.description}</dd>
                  <dt className="text-zinc-500">Quantity</dt><dd>{originalRow?.quantity ?? "—"} → {command.quantity}</dd>
                  <dt className="text-zinc-500">Unit Price</dt><dd>{formatServiceCost(originalRow?.unit_price)} → {formatServiceCost(command.unit_price)}</dd>
                  <dt className="text-zinc-500">Amount</dt><dd>{formatServiceCost(originalAmount)} → {formatServiceCost(nextAmount)}</dd>
                </dl>
              </li>
            );
          }
          if (command.action === "remove") {
            return (
              <li key={`${command.action}:${command.id}`} data-testid={`review-custom-cost-${command.action}-${command.id}`}>
                <span className="font-medium">Remove Custom Cost:</span>{" "}
                {description} ({originalRow?.quantity ?? "—"} × {formatServiceCost(originalRow?.unit_price)} = {formatServiceCost(originalAmount)})
              </li>
            );
          }
          return (
            <li key={`${command.action}:${command.id || index}`} data-testid={`review-custom-cost-${command.action}-${command.id || index}`}>
              <span className="font-medium capitalize">{command.action}</span>{" Custom Cost: "}
              {description}
              {draft && nextAmount !== undefined ? <span>{` = ${formatServiceCost(nextAmount)}`}</span> : null}
              {` (${command.quantity} × ${formatServiceCost(command.unit_price)})`}
            </li>
          );
        })}
        {mutation?.discount ? (
          <li data-testid="review-service-cost-discount">
            <div className="font-medium">Discount</div>
            <div className="mt-1 text-xs" data-testid="review-service-cost-discount-state">
              {discountState(originalDiscount)} → {discountState(mutation.discount)}
            </div>
          </li>
        ) : null}
      </ul>
      <dl className="ml-auto mt-3 grid max-w-lg grid-cols-[1fr_auto] gap-x-8 gap-y-2 rounded-sm border p-4 text-sm" data-testid="review-service-cost-summary">
        <dt className="text-zinc-500">Gross Service Cost</dt><dd className="text-right tabular-nums">{formatServiceCost(summary.gross)}</dd>
        <dt className="text-zinc-500">Discount Amount</dt><dd className="text-right tabular-nums">{formatServiceCost(summary.discountAmount)}</dd>
        <dt className="font-semibold">Final Service Cost</dt><dd className="text-right font-semibold tabular-nums text-primary">{formatServiceCost(summary.final)}</dd>
      </dl>
    </section>
  );
}
